import { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Spinner, 
  TableColumn
} from '@heroui/react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableCell 
} from '@heroui/react';
import { 
  Pagination 
} from '@heroui/react';
import { 
  Dropdown, 
  DropdownTrigger, 
  DropdownMenu, 
  DropdownItem 
} from '@heroui/react';
import { Button } from '@heroui/react';
import { Input } from '@heroui/react';
import { 
  Select, 
  SelectItem 
} from '@heroui/react';
import BodegaService from '@/services/bodega/bodega.service';
import type { Bodega, BodegaRequest } from '@/types/bodega.model';
import BodegaModal from '@/components/bodega/newModal';
import BodegaEditModal from '@/components/bodega/editModal';
import BodegaDetailModal from '@/components/bodega/detailModal';
import DeleteBodegaModal from '@/components/bodega/deleteModal';

const BodegaPage = () => {
  const [bodegas, setBodegas] = useState<Bodega[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentBodega, setCurrentBodega] = useState<Bodega | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nombre');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Fetch bodegas
  useEffect(() => {
    const fetchBodegas = async () => {
      try {
        setLoading(true);
        const response = await BodegaService.getAll({
          page: currentPage,
          limit: itemsPerPage,
          filter: searchTerm,
          sortBy,
          sortOrder
        });
        
        setBodegas(response.data);
        setTotalItems(response.meta.pagination.total);
        setTotalPages(response.meta.pagination.pageCount);
        setError(null);
      } catch (err) {
        setError('Error al cargar las bodegas');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBodegas();
    return () => { setBodegas([]); }
  }, [currentPage, itemsPerPage, searchTerm, sortBy, sortOrder]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      setItemsPerPage(numValue);
      setCurrentPage(1); // Reset to first page when changing items per page
    }
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle dropdown actions
  const handleEdit = (bodega: Bodega) => {
    setCurrentBodega(bodega);
    setIsEditModalOpen(true);
  };

  const handleDelete = (bodega: Bodega) => {
    setCurrentBodega(bodega);
    setIsDeleteModalOpen(true);
  };

  const handleViewDetail = (bodega: Bodega) => {
    setCurrentBodega(bodega);
    setIsDetailModalOpen(true);
  };

  // Handle deleting a bodega
  const handleDeleteBodega = async (id: string | number) => {
    setIsSubmitting(true);
    try {
      // Call the service to delete the bodega
      await BodegaService.delete(id);
      
      // Refresh the list of bodegas
      const response = await BodegaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        filter: searchTerm,
        sortBy,
        sortOrder
      });
      
      setBodegas(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al eliminar la bodega');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle modals
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
    if (!isEditModalOpen) {
      // When opening the edit modal, make sure we have currentBodega
    } else {
      // When closing the edit modal, clear the current bodega
      setCurrentBodega(undefined);
    }
  };

  const toggleDetailModal = () => {
    setIsDetailModalOpen(!isDetailModalOpen);
    if (!isDetailModalOpen) {
      // When opening the detail modal, make sure we have currentBodega
    } else {
      // When closing the detail modal, clear the current bodega
      setCurrentBodega(undefined);
    }
  };

  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
    if (!isDeleteModalOpen) {
      // When opening the delete modal, make sure we have currentBodega
    } else {
      // When closing the delete modal, clear the current bodega
      setCurrentBodega(undefined);
    }
  };

  // Calculate sort indicator
  const getSortIndicator = (field: string) => {
    if (sortBy !== field) return '';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Handle creating a new bodega
  const handleCreateBodega = async (data: Omit<BodegaRequest['data'], 'tenantId' | 'locale' | 'localizations' | 'foto'>) => {
    setIsSubmitting(true);
    try {
      // Prepare the data for submission
      const bodegaData: BodegaRequest['data'] = {
        ...data
      };

      // Call the service to create the bodega
      await BodegaService.create({ data: bodegaData });
      
      // Refresh the list of bodegas
      const response = await BodegaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        filter: searchTerm,
        sortBy,
        sortOrder
      });
      
      setBodegas(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al crear la bodega');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle updating a bodega
  const handleUpdateBodega = async (id: string | number, data: Omit<BodegaRequest['data'], 'tenantId' | 'locale' | 'localizations' | 'foto'>) => {
    setIsSubmitting(true);
    try {
      // Prepare the data for submission
      const bodegaData: BodegaRequest['data'] = {
        ...data
      };

      // Call the service to update the bodega
      await BodegaService.update(id, { data: bodegaData });
      
      // Refresh the list of bodegas
      const response = await BodegaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        filter: searchTerm,
        sortBy,
        sortOrder
      });
      
      setBodegas(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al actualizar la bodega');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-10 px-4">
      <Card className="w-full">
        <CardHeader className="border-b flex flex-direction-row justify-between items-center p-6">
          <h3 className="text-2xl font-bold">Gestión de Bodegas</h3>          
          <p className="text-default-500">Administra las bodegas de tu sistema</p>
        </CardHeader>
        <CardBody className="p-6">
          {/* Filters and Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="relative w-full md:w-64">
                <Input
                  placeholder="Buscar bodegas..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              
              <Select 
                aria-label="Elementos por página"
                className="w-[180px]"
                selectedKeys={[itemsPerPage.toString()]}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys).join('');
                  if (value) handleItemsPerPageChange(value);
                }}
              >
                <SelectItem key="5">5 por página</SelectItem>
                <SelectItem key="10">10 por página</SelectItem>
                <SelectItem key="20">20 por página</SelectItem>
                <SelectItem key="50">50 por página</SelectItem>
              </Select>
            </div>
            
            <Button 
              className="w-full md:w-auto"
              onPress={toggleModal}
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Nueva Bodega
            </Button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center text-red-500">
                <p>{error}</p>
                <Button 
                  variant="bordered" 
                  onPress={() => window.location.reload()}
                  className="mt-4"
                >
                  Reintentar
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableColumn key="acciones" className="px-3 py-2 text-left">Acciones</TableColumn>
                    <TableColumn 
                      key="codigo"
                      onClick={() => handleSortChange('codigo')}
                      allowsSorting
                    >
                      Código {getSortIndicator('codigo')}
                    </TableColumn>
                    <TableColumn 
                      key="nombre"                      
                      onClick={() => handleSortChange('nombre')}
                      allowsSorting
                    >
                      Nombre {getSortIndicator('nombre')}
                    </TableColumn>
                    <TableColumn 
                      key="estado"                      
                      onClick={() => handleSortChange('estado')}
                      allowsSorting
                    >
                      Estado {getSortIndicator('estado')}
                    </TableColumn>
                </TableHeader>
                <TableBody
                  isLoading={loading}
                  loadingContent={<Spinner label="Loading..." />}
                  items={bodegas}
                >
                  {bodegas.length > 0 ? (
                    bodegas.map((bodega) => (
                      <TableRow key={bodega.id}>
                                                <TableCell className="text-left">
                          <Dropdown>
                            <DropdownTrigger>
                              <Button variant="light" className="h-8 w-8 p-0">
                                <span className="sr-only">Abrir menú</span>
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                  />
                                </svg>
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu aria-label="Acciones de la bodega">
                              <DropdownItem key="edit" onClick={() => bodega.documentId !== undefined && handleEdit(bodega)}>
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                                Editar
                              </DropdownItem>
                              <DropdownItem key="view" onClick={() => bodega.documentId !== undefined && handleViewDetail(bodega)}>
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                Ver detalle
                              </DropdownItem>
                              <DropdownItem 
                                key="delete" 
                                className="text-danger"
                                color="danger"
                                onClick={() => bodega.documentId !== undefined && handleDelete(bodega)}
                              >
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                                Eliminar
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </TableCell>
                        <TableCell className="font-medium">{bodega.codigo}</TableCell>
                        <TableCell>{bodega.nombre}</TableCell>
                        <TableCell>
                          <span className={
                            bodega.estado === 'Activa' 
                              ? 'bg-success-500 text-white px-2 py-1 rounded text-sm' 
                              : 'bg-danger-500 text-white px-2 py-1 rounded text-sm'
                          }>
                            {bodega.estado}
                          </span>
                        </TableCell>

                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-10">
                        No se encontraron bodegas
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && !loading && (
            <div className="flex flex-col sm:flex-row items-center justify-between pt-6">
              <div className="text-sm text-default-500 mb-4 sm:mb-0">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} resultados
              </div>
              <Pagination
                total={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                showControls
              />
            </div>
          )}
        </CardBody>
      </Card>
      
      {/* Bodega Modal */}
      <BodegaModal 
        isOpen={isModalOpen} 
        onClose={toggleModal} 
        onSubmit={handleCreateBodega}
        loading={isSubmitting}
      />
      
      {/* Bodega Edit Modal */}
      <BodegaEditModal 
        isOpen={isEditModalOpen} 
        onClose={toggleEditModal} 
        onSubmit={handleUpdateBodega}
        loading={isSubmitting}
        bodega={currentBodega}
      />
      
      {/* Bodega Detail Modal */}
      <BodegaDetailModal 
        isOpen={isDetailModalOpen} 
        onClose={toggleDetailModal}
        bodega={currentBodega}
      />
      
      {/* Bodega Delete Modal */}
      <DeleteBodegaModal 
        isOpen={isDeleteModalOpen} 
        onClose={toggleDeleteModal}
        onDelete={handleDeleteBodega}
        bodega={currentBodega}
      />
    </div>
  );
};

export default BodegaPage;